/**
 * SEASON HELPERS — pure functions derived from the `season` block's
 * `sessions` list (see lib/content.ts). Nothing here reads the CMS or the
 * network; every page and the admin editor call these with whatever
 * `sessions` they already have so the "starts Monday …" line, the session
 * counts and "what's next" can never drift from the schedule a host typed
 * in.
 *
 * Dates are plain ISO strings (YYYY-MM-DD) and are always read as UTC
 * midnight — same trick `formatEventDates` in @alight/contracts uses — so a
 * date never shifts by a viewer's timezone.
 */
import { todayInSydney } from "@alight/contracts";
import type { Block } from "./content";

export type Session = Block<"season">["sessions"][number];
export type SessionKind = Session["kind"];

const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Parse "YYYY-MM-DD" as UTC midnight, or null for anything else (blank, malformed). */
function parseISO(date: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) return null;
  const d = new Date(`${date.trim()}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Mon", or "" for an unparseable date — used to flag a mistyped date in the editor. */
export function weekdayShort(date: string): string {
  const d = parseISO(date);
  return d ? DOW_SHORT[d.getUTCDay()]! : "";
}

/** "Monday", or "" for an unparseable date. */
export function weekdayLong(date: string): string {
  const d = parseISO(date);
  return d ? DOW_LONG[d.getUTCDay()]! : "";
}

/** "9 Nov 2026", or "" for an unparseable date. */
export function formatShortDate(date: string): string {
  const d = parseISO(date);
  if (!d) return "";
  return `${d.getUTCDate()} ${MON_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "9 Nov" — no year, for a row inside a month group that already has one. */
export function formatDayMonth(date: string): string {
  const d = parseISO(date);
  if (!d) return "";
  return `${d.getUTCDate()} ${MON_SHORT[d.getUTCMonth()]}`;
}

/** "Mon 9 Nov" — the sessions page's row label. */
export function formatRowLabel(date: string): string {
  const short = weekdayShort(date);
  const dayMonth = formatDayMonth(date);
  if (!short || !dayMonth) return "";
  return `${short} ${dayMonth}`;
}

/** "9 November 2026", or "" for an unparseable date. */
export function formatLongDate(date: string): string {
  const d = parseISO(date);
  if (!d) return "";
  return `${d.getUTCDate()} ${MON_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Mon 9 Nov 2026" — the admin editor's weekday hint next to a date field. */
export function weekdayLabel(date: string): string {
  const short = weekdayShort(date);
  const formatted = formatShortDate(date);
  if (!short || !formatted) return "";
  return `${short} ${formatted}`;
}

/** "November 2026" — the sessions page's group headings. */
export function monthLabel(date: string): string {
  const d = parseISO(date);
  if (!d) return "";
  return `${MON_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Chronological order — sessions with an unparseable date sort last, stably. */
export function sortSessionsByDate(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => {
    const da = parseISO(a.date);
    const db = parseISO(b.date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
}

/** Sessions grouped by month, in chronological order — for the sessions page. */
export function groupSessionsByMonth(sessions: Session[]): { label: string; sessions: Session[] }[] {
  const sorted = sortSessionsByDate(sessions);
  const groups: { label: string; sessions: Session[] }[] = [];
  for (const session of sorted) {
    const label = monthLabel(session.date) || "Other dates";
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.sessions.push(session);
    } else {
      groups.push({ label, sessions: [session] });
    }
  }
  return groups;
}

/** "Starts Monday 9 November 2026" from the earliest dated session. */
export function seasonStartLabel(sessions: Session[]): string {
  const first = sortSessionsByDate(sessions).find((s) => parseISO(s.date));
  if (!first) return "";
  return `Starts ${weekdayLong(first.date)} ${formatLongDate(first.date)}`;
}

/** The latest dated session — usually the last Campus Time date. */
export function seasonEndSession(sessions: Session[]): Session | null {
  const dated = sortSessionsByDate(sessions).filter((s) => parseISO(s.date));
  return dated[dated.length - 1] ?? null;
}

/** "Ends Monday 8 February 2027", or "" when there are no dated sessions. */
export function seasonEndLabel(sessions: Session[]): string {
  const last = seasonEndSession(sessions);
  return last ? `Ends ${weekdayLong(last.date)} ${formatLongDate(last.date)}` : "";
}

/** How many sessions of a given kind — e.g. 8 Connect sessions, 3 Campus Time. */
export function countByKind(sessions: Session[], kind: SessionKind): number {
  return sessions.filter((s) => s.kind === kind).length;
}

/**
 * The next session that hasn't happened yet, today in Australia/Sydney.
 * Breaks are skipped — "what's coming up" means the next thing to actually
 * go to, not the next Monday off.
 */
export function nextUpcomingSession(sessions: Session[], today: string = todayInSydney()): Session | null {
  const upcoming = sortSessionsByDate(sessions).filter((s) => {
    const d = parseISO(s.date);
    return d && s.kind !== "break" && s.date >= today;
  });
  return upcoming[0] ?? null;
}

export interface SeasonSummary {
  startLabel: string;
  endLabel: string;
  sessionCount: number;
  campusCount: number;
  next: Session | null;
}

/** Everything the pages need in one call, so they never re-derive it differently. */
export function summariseSeason(sessions: Session[], today: string = todayInSydney()): SeasonSummary {
  return {
    startLabel: seasonStartLabel(sessions),
    endLabel: seasonEndLabel(sessions),
    sessionCount: countByKind(sessions, "session"),
    campusCount: countByKind(sessions, "campus"),
    next: nextUpcomingSession(sessions, today),
  };
}
